import { useState, useMemo } from 'react';
import { X, ChevronDown, CheckCircle2, Hourglass, Info } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  procedureGestionProjetsClientSections,
  type ProcedureClientTask,
} from '../data/procedureGestionProjetsClientTasks';

interface ProcedureGestionProjetsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function renderTaskLabel(text: string) {
  const parts = text.split(/(\*\*\*[^*]+\*\*\*)/g);
  return parts.map((part, i) => {
    const m = part.match(/^\*\*\*(.+)\*\*\*$/);
    if (m) {
      return (
        <strong key={i} className="font-semibold text-white">
          {m[1]}
        </strong>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

function TaskRow({ task }: { task: ProcedureClientTask }) {
  const [infoOpen, setInfoOpen] = useState(false);
  const done = task.status === 'done';
  const hasInfo = Boolean(task.info);

  return (
    <div className="border-b border-slate-700/80 py-2.5 pl-1 pr-2">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0" aria-hidden>
          {done ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-400/90" strokeWidth={1.75} />
          ) : (
            <Hourglass className="h-5 w-5 text-slate-400" strokeWidth={1.75} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <p className="min-w-0 flex-1 text-[13px] leading-snug text-slate-100">
              {renderTaskLabel(task.label)}
            </p>
            {hasInfo && (
              <button
                type="button"
                onClick={() => setInfoOpen((v) => !v)}
                className="mt-0.5 shrink-0 rounded-md p-0.5 text-sky-400/95 transition-colors hover:bg-slate-800 hover:text-sky-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/80"
                aria-expanded={infoOpen}
                aria-label="Informations sur cette étape"
                title={infoOpen ? 'Masquer les informations' : 'Afficher les informations'}
              >
                <Info className="h-4 w-4" strokeWidth={2} aria-hidden />
              </button>
            )}
          </div>
          {infoOpen && task.info && (
            <p
              className="mt-2 rounded-lg border border-slate-700/90 bg-slate-800/50 px-3 py-2 text-[13px] leading-relaxed text-slate-300"
              role="region"
              aria-live="polite"
            >
              {task.info}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProcedureGestionProjetsModal({ isOpen, onClose }: ProcedureGestionProjetsModalProps) {
  const initialOpen = useMemo(() => {
    const o: Record<string, boolean> = {};
    for (const s of procedureGestionProjetsClientSections) {
      o[s.id] = true;
    }
    return o;
  }, []);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(initialOpen);

  const toggle = (id: string) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Fermer"
        className="absolute inset-0 bg-black/75 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="procedure-gestion-projets-title"
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative z-10 flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-700/80 bg-zinc-950 shadow-2xl"
      >
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-700/80 px-5 py-4">
            <div>
              <h2 id="procedure-gestion-projets-title" className="text-lg font-bold tracking-tight text-white">
                Procédure de gestion de projet (base de gestion de projet)
              </h2>
              <p className="mt-1 text-sm text-slate-400">
                Tâches indicatives — prise en charge nouveau client et projets client (représentation statique).
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-5">
            <div className="space-y-2">
              {procedureGestionProjetsClientSections.map((section) => {
                const expanded = openSections[section.id] !== false;
                return (
                  <div
                    key={section.id}
                    className="overflow-hidden rounded-xl border border-slate-800 bg-zinc-900/90"
                  >
                    <button
                      type="button"
                      onClick={() => toggle(section.id)}
                      className="flex w-full items-center gap-2 px-3 py-3 text-left text-[15px] font-semibold text-white transition-colors hover:bg-slate-800/60"
                    >
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${expanded ? 'rotate-0' : '-rotate-90'}`}
                      />
                      <span>{section.title}</span>
                    </button>
                    {expanded && (
                      <div className="border-t border-slate-800">
                        <div className="px-3 pb-1 pt-0">
                          {section.tasks.map((task) => (
                            <TaskRow key={task.id} task={task} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div
              className="mt-6 flex gap-3 rounded-xl border-2 border-amber-400/90 bg-amber-500/15 px-4 py-4 shadow-[0_0_24px_rgba(251,191,36,0.12)]"
              role="note"
            >
              <Info className="mt-0.5 h-6 w-6 shrink-0 text-amber-300" aria-hidden />
              <div>
                <p className="text-sm font-bold uppercase tracking-wide text-amber-200">Note importante</p>
                <p className="mt-2 text-[15px] font-semibold leading-relaxed text-amber-50">
                  Le technicien a la responsabilité d&apos;informer le DDC à la fin d&apos;un projet. Le DDC a la
                  responsabilité de confirmer la réception de l&apos;information et de s&apos;informer de l&apos;avancement
                  s&apos;il a des questions ou des préoccupations.
                </p>
              </div>
            </div>
          </div>
      </motion.div>
    </div>
  );
}
